import React, { useState } from 'react';
import {
    Box, Button, IconButton, TextField, Typography, Table, TableHead, TableBody, TableRow, TableCell,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';

/**
 * Licence tiers of a plan (20 Oct 2026) — a price per licence that falls as the number
 * of licences grows. Every value is editable: the tier names, the licence count each
 * tier starts from, and the price; tiers can be added and removed.
 *
 *   value: [{ name, from, usd_per_licence }]  — the first tier starts from 0
 *
 * The API validates and sorts them; validateLicenceTiers below gives the same answer
 * in the form, before saving.
 */

const fmtCount = (n) => Number(n).toLocaleString('en-US');

/** The rows as entered; anything else (a legacy value) is treated as no tiers. */
export const normalizeLicenceTiers = (val) => (Array.isArray(val) ? val : []);

/** Same rules as the API. '' when valid. */
export const validateLicenceTiers = (val) => {
    const rows = normalizeLicenceTiers(val);
    if (rows.length === 0) return '';
    const parsed = rows.map((r) => ({ name: String(r.name || '').trim(), from: Number(r.from), price: Number(r.usd_per_licence) }));
    for (const [i, r] of parsed.entries()) {
        if (!r.name) return `Tier ${i + 1}: enter a name.`;
        if (String(rows[i].from) === '' || !Number.isInteger(r.from) || r.from < 0) return `${r.name}: "From" must be a whole number of licences, 0 or more.`;
        if (!(r.price > 0) || Math.abs(Math.round(r.price * 100) - r.price * 100) > 1e-6) return `${r.name}: the price must be more than 0, with at most 2 decimal places.`;
    }
    const froms = parsed.map((r) => r.from).sort((a, b) => a - b);
    if (froms[0] !== 0) return 'The first tier must start from 0 licences.';
    if (new Set(froms).size !== froms.length) return 'Two tiers start from the same number of licences.';
    return '';
};

/** "Until" of each row, from the next tier's "From" — shown so the ranges are clear. */
const untilOf = (rows, row) => {
    const from = Number(row.from);
    const next = rows.map((r) => Number(r.from)).filter((f) => Number.isFinite(f) && f > from).sort((a, b) => a - b)[0];
    return next === undefined ? 'and above' : `to ${fmtCount(next - 1)}`;
};

const LicenceTiersEditor = ({ value, onChange, disabled, helperText, error }) => {
    const rows = normalizeLicenceTiers(value);
    const set = (i, key, v) => onChange(rows.map((r, j) => (j === i ? { ...r, [key]: v } : r)));
    const add = () => {
        const last = rows.length ? Math.max(...rows.map((r) => Number(r.from) || 0)) : -1;
        onChange([...rows, { name: '', from: rows.length ? last + 1 : 0, usd_per_licence: '' }]);
    };
    const remove = (i) => onChange(rows.filter((_, j) => j !== i));

    return (
        <Box>
            {rows.length > 0 && (
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Tier name</TableCell>
                            <TableCell>From (licences)</TableCell>
                            <TableCell/>
                            <TableCell>US$ per licence</TableCell>
                            <TableCell/>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.map((r, i) => (
                            <TableRow key={i}>
                                <TableCell style={{ width: '28%' }}>
                                    <TextField size="small" variant="outlined" fullWidth value={r.name ?? ''} disabled={disabled}
                                        placeholder="e.g. Growth" onChange={(e) => set(i, 'name', e.target.value)}/>
                                </TableCell>
                                <TableCell style={{ width: '22%' }}>
                                    <TextField size="small" variant="outlined" fullWidth type="number" value={r.from ?? ''} disabled={disabled}
                                        inputProps={{ min: 0, step: 1 }} onChange={(e) => set(i, 'from', e.target.value === '' ? '' : Number(e.target.value))}/>
                                </TableCell>
                                <TableCell style={{ width: '18%' }}>
                                    <Typography variant="caption" color="textSecondary">{untilOf(rows, r)}</Typography>
                                </TableCell>
                                <TableCell style={{ width: '22%' }}>
                                    <TextField size="small" variant="outlined" fullWidth type="number" value={r.usd_per_licence ?? ''} disabled={disabled}
                                        inputProps={{ min: 0, step: 0.01 }} onChange={(e) => set(i, 'usd_per_licence', e.target.value === '' ? '' : Number(e.target.value))}/>
                                </TableCell>
                                <TableCell style={{ width: '10%' }} align="right">
                                    <IconButton size="small" onClick={() => remove(i)} disabled={disabled} aria-label="Remove tier"><CloseIcon fontSize="small"/></IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            )}
            <Box display="flex" alignItems="center" style={{ gap: 12, marginTop: 8 }}>
                <Button size="small" variant="outlined" color="primary" onClick={add} disabled={disabled}>+ Add tier</Button>
                {rows.length === 0 && <Typography variant="body2" color="textSecondary">No tiers.</Typography>}
            </Box>
            {(error || helperText) && (
                <Typography variant="caption" component="p" style={{ marginTop: 6, color: error ? '#d32f2f' : undefined }} color={error ? undefined : 'textSecondary'}>
                    {error || helperText}
                </Typography>
            )}
        </Box>
    );
};

/**
 * Tiers per sign-up solution, for a plan that sells more than one at different prices
 * (the partner P2 plan: P2B). value: { [solution]: [tiers] }.
 */
export const LicenceTiersBySolutionEditor = ({ value, onChange, disabled, helperText, error }) => {
    const map = value && typeof value === 'object' && !Array.isArray(value) ? value : {};
    const [newSolution, setNewSolution] = useState('');
    const add = () => {
        const key = newSolution.trim().toUpperCase();
        if (!key || map[key]) return;
        onChange({ ...map, [key]: [] });
        setNewSolution('');
    };
    const remove = (key) => {
        const next = { ...map };
        delete next[key];
        onChange(next);
    };
    return (
        <Box>
            {Object.keys(map).map((key) => (
                <Box key={key} mb={2} p={1.5} style={{ border: '1px solid #e0e0e0', borderRadius: 8 }}>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                        <Typography style={{ fontWeight: 600 }}>{key}</Typography>
                        <Button size="small" onClick={() => remove(key)} disabled={disabled}>Remove {key}</Button>
                    </Box>
                    <LicenceTiersEditor value={map[key]} disabled={disabled}
                        onChange={(tiers) => onChange({ ...map, [key]: tiers })}/>
                </Box>
            ))}
            <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                <TextField size="small" variant="outlined" label="Solution (e.g. P2B)" value={newSolution} disabled={disabled}
                    onChange={(e) => setNewSolution(e.target.value)}/>
                <Button size="small" variant="outlined" color="primary" onClick={add} disabled={disabled || !newSolution.trim()}>+ Add solution tiers</Button>
            </Box>
            {(error || helperText) && (
                <Typography variant="caption" component="p" style={{ marginTop: 6, color: error ? '#d32f2f' : undefined }} color={error ? undefined : 'textSecondary'}>
                    {error || helperText}
                </Typography>
            )}
        </Box>
    );
};

/** '' when every solution's tiers are valid. */
export const validateLicenceTiersBySolution = (val) => {
    const map = val && typeof val === 'object' && !Array.isArray(val) ? val : {};
    for (const [key, tiers] of Object.entries(map)) {
        if (!normalizeLicenceTiers(tiers).length) return `${key}: add its tiers, or remove ${key}.`;
        const msg = validateLicenceTiers(tiers);
        if (msg) return `${key}: ${msg}`;
    }
    return '';
};

export default LicenceTiersEditor;
